import { useUserAuth } from './useUserAuth';

/**
 * All Auth0 role strings used across Lucid portals.
 * Use these constants instead of raw strings when checking roles.
 */
export const USER_ROLES = {
  // Admin
  LUCID_ADMIN: 'Lucid Admin',
  // Importer HQ roles
  IMPORTER_ADMIN: 'Importer-Admin-R1',
  IMPORTER_MANAGER: 'Importer-Manager-R2',
  IMPORTER_USER: 'Importer-User-R3',
  IMPORTER_SERVICE_MANAGER: 'Importer-Service-Manager',
  IMPORTER_TECHNICIAN: 'Importer-Technician',
  IMPORTER_GROUP_SERVICE_MANAGER: 'Importer-Group-Service-Manager',
  // Importer AR roles
  IMPORTER_AR_SERVICE_MANAGER: 'Importer-AR-Service-Manager',
  IMPORTER_AR_TECHNICIAN: 'Importer-AR-Technician',
  // Agency Sales roles
  AGENCY_ADMIN: 'Agency-Admin-R4',
  AGENCY_MANAGER: 'Agency-Manager-R5',
  AGENCY_USER: 'Agency-User-R6',
  // Agency AR Support Agent roles
  AR_SUPPORT_AGENT_SERVICE_MANAGER: 'AR-Support-Agent-Service-Manager',
  AR_SUPPORT_AGENT_TECHNICIAN: 'AR-Support-Agent-Technician',
  AR_SUPPORT_AGENT_GROUP_MANAGER: 'AR-Support-Agent-Group-Manager',
} as const;

// Pre-built lowercase sets for precise group membership checks.
// Using sets avoids substring false-positives (e.g. 'AR-Support-Agent' containing 'agency').
const AGENCY_ROLE_STRINGS = new Set([
  USER_ROLES.AGENCY_ADMIN.toLowerCase(),
  USER_ROLES.AGENCY_MANAGER.toLowerCase(),
  USER_ROLES.AGENCY_USER.toLowerCase(),
]);

const IMPORTER_HQ_ROLE_STRINGS = new Set([
  USER_ROLES.IMPORTER_ADMIN.toLowerCase(),
  USER_ROLES.IMPORTER_MANAGER.toLowerCase(),
  USER_ROLES.IMPORTER_USER.toLowerCase(),
  USER_ROLES.IMPORTER_SERVICE_MANAGER.toLowerCase(),
  USER_ROLES.IMPORTER_TECHNICIAN.toLowerCase(),
  USER_ROLES.IMPORTER_GROUP_SERVICE_MANAGER.toLowerCase(),
]);

const IMPORTER_AR_ROLE_STRINGS = new Set([
  USER_ROLES.IMPORTER_AR_SERVICE_MANAGER.toLowerCase(),
  USER_ROLES.IMPORTER_AR_TECHNICIAN.toLowerCase(),
]);

const AR_SUPPORT_AGENT_ROLE_STRINGS = new Set([
  USER_ROLES.AR_SUPPORT_AGENT_SERVICE_MANAGER.toLowerCase(),
  USER_ROLES.AR_SUPPORT_AGENT_TECHNICIAN.toLowerCase(),
  USER_ROLES.AR_SUPPORT_AGENT_GROUP_MANAGER.toLowerCase(),
]);

export type UseUserRoleResult = {
  /** True for any Importer-HQ role (Admin, Manager, User, Service-Manager, Technician, Group-Service-Manager) */
  isImporter: boolean;
  /** True for any Importer-AR role (AR-Service-Manager, AR-Technician) */
  isImporterAR: boolean;
  /** True for any Agency-* role (Admin, Manager, User) */
  isAgencySales: boolean;
  /** True for any AR-Support-Agent-* role */
  isARSupportAgent: boolean;
  /** True for the Lucid Admin role */
  isAdmin: boolean;
  /** Raw roles array from JWT claims */
  roles: string[];
  /** True while Auth0 is still resolving tokens */
  isLoading: boolean;
};

/**
 * Returns the current user's role flags, derived from Auth0 JWT claims.
 *
 * @param rolesNamespace - Auth0 custom claims namespace for roles (e.g. `https://your-domain/roles`)
 * @returns `{ isAdmin, isImporter, isImporterAR, isAgencySales, isARSupportAgent, roles, isLoading }`
 *
 * @example
 * ```tsx
 * // In your app, bind the namespace once in a wrapper hook:
 * const useAppUserRole = () => useUserRole(`https://${env.AUTH0_DOMAIN}/roles`);
 * ```
 */
export const useUserRole = (rolesNamespace?: string): UseUserRoleResult => {
  const { roles, isLoading } = useUserAuth(rolesNamespace);

  // Normalise defensively — guards against null/undefined/non-string entries
  const normalised = roles.flatMap((r) => (typeof r === 'string' ? [r.toLowerCase()] : []));

  const isImporter = normalised.some((r) => IMPORTER_HQ_ROLE_STRINGS.has(r));
  const isImporterAR = normalised.some((r) => IMPORTER_AR_ROLE_STRINGS.has(r));
  const isAgencySales = normalised.some((r) => AGENCY_ROLE_STRINGS.has(r));
  const isARSupportAgent = normalised.some((r) => AR_SUPPORT_AGENT_ROLE_STRINGS.has(r));

  return {
    isAdmin: normalised.includes(USER_ROLES.LUCID_ADMIN.toLowerCase()),
    isImporter,
    isImporterAR,
    isAgencySales,
    isARSupportAgent,
    roles,
    isLoading,
  };
};
